import React, { useState, useEffect, useContext } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { TrendingUp, Users, Receipt, IndianRupee, Filter, Calendar, Award, AlertTriangle, RefreshCw, ChevronDown, BarChart3, Star, TrendingDown, Store } from "lucide-react";
import { staffAPI, storeAPI } from "../../api/apiService";
import { NotificationContext } from "../../context/NotificationContext";
import { format } from "date-fns";
import EmptyState from "../../components/EmptyState";

const getRoleLabel = (roles) => {
    if (!roles || roles.length === 0) return 'Sales Executive';
    const roleName = roles[0].name;
    return roleName.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

export default function EmployeeTracking() {
    const { addNotification } = useContext(NotificationContext);
    const [performanceData, setPerformanceData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [timeFilter, setTimeFilter] = useState("total"); // total, today, this_week, this_month
    const [sortBy, setSortBy] = useState("bills"); // bills, customers, revenue
    const [stores, setStores] = useState([]);
    const [selectedStore, setSelectedStore] = useState("");

    const fetchStores = async () => {
        try {
            const res = await storeAPI.getAll();
            setStores(res.data || []);
        } catch (err) {
            console.error("Failed to load stores:", err);
        }
    };

    const fetchPerformance = async () => {
        setLoading(true);
        try {
            const res = await staffAPI.getStaffPerformance(selectedStore);
            setPerformanceData(res.data);
        } catch (err) {
            console.error("Failed to fetch performance:", err);
            addNotification("Failed to load performance metrics", "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStores();
    }, []);

    useEffect(() => {
        fetchPerformance();
    }, [timeFilter, selectedStore]);

    const sortedData = [...performanceData].sort((a, b) => {
        if (sortBy === "bills") return (b.total_bills || 0) - (a.total_bills || 0);
        if (sortBy === "customers") return (b.total_customers || 0) - (a.total_customers || 0);
        if (sortBy === "revenue") return (b.total_revenue || 0) - (a.total_revenue || 0);
        return 0;
    });

    const topPerformer = sortedData.length > 0 ? sortedData[0] : null;
    const lowPerformer = sortedData.length > 1 ? sortedData[sortedData.length - 1] : null;

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-slate-900 p-4 md:p-6">
            <div className="max-w-7xl mx-auto space-y-6">
                
                {/* Header */}
                <div className="bg-white dark:bg-slate-800 p-8 rounded-3xl shadow-sm border border-gray-100 dark:border-slate-700">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                        <div className="flex items-center gap-5">
                            <div className="p-4 bg-indigo-600 rounded-2xl text-white shadow-lg shadow-indigo-500/20">
                                <TrendingUp className="w-10 h-10" />
                            </div>
                            <div>
                                <h1 className="text-3xl font-black text-gray-900 dark:text-white">Employee Tracking</h1>
                                <p className="text-gray-500 dark:text-slate-400 font-medium tracking-wide">Analyze and compare team performance metrics</p>
                            </div>
                        </div>

                        {/* Filters */}
                        <div className="flex flex-col md:flex-row items-stretch md:items-center gap-4">
                            {/* Store Selector */}
                            <div className="relative">
                                <Store className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <select 
                                    className="pl-10 pr-8 py-2.5 bg-gray-50 dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-xl text-sm font-bold appearance-none dark:text-white outline-none focus:ring-2 focus:ring-indigo-500 transition-all cursor-pointer"
                                    value={selectedStore}
                                    onChange={(e) => setSelectedStore(e.target.value)}
                                >
                                    <option value="">All Stores</option>
                                    {stores.map(store => (
                                        <option key={store.id} value={store.id}>{store.name}</option>
                                    ))}
                                </select>
                                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                            </div>

                            {/* Time Filters */}
                            <div className="flex items-center gap-2 bg-gray-50 dark:bg-slate-900 p-1.5 rounded-xl border border-gray-150 dark:border-slate-700">
                                <FilterChip active={timeFilter === "total"} label="All Time" onClick={() => setTimeFilter("total")} />
                                <FilterChip active={timeFilter === "today"} label="Today" onClick={() => setTimeFilter("today")} />
                                <FilterChip active={timeFilter === "this_week"} label="This Week" onClick={() => setTimeFilter("this_week")} />
                                <FilterChip active={timeFilter === "this_month"} label="This Month" onClick={() => setTimeFilter("this_month")} />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Top Level Metrics */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <HighlightCard 
                        title="Top Performer" 
                        employee={topPerformer} 
                        metric={sortBy === "bills" ? "Bills" : sortBy === "customers" ? "Customers" : "Revenue"}
                        type="top"
                    />
                    <MetricCard 
                        label="Average Efficiency" 
                        value={performanceData.length ? (performanceData.reduce((acc, s) => acc + (s.total_bills || 0), 0) / performanceData.length).toFixed(1) : 0} 
                        sub="Bills per employee"
                        icon={BarChart3}
                        color="blue"
                    />
                    <HighlightCard 
                        title="Needs Improvement" 
                        employee={lowPerformer} 
                        metric={sortBy === "bills" ? "Bills" : sortBy === "customers" ? "Customers" : "Revenue"}
                        type="low"
                    />
                </div>

                {/* Sorting Controls */}
                <div className="flex flex-wrap items-center gap-4">
                    <p className="text-sm font-bold text-gray-400 uppercase tracking-widest ml-1">Sort Performance By:</p>
                    <SortButton active={sortBy === "bills"} label="Highest Bills" onClick={() => setSortBy("bills")} icon={Receipt} />
                    <SortButton active={sortBy === "customers"} label="Highest Customers" onClick={() => setSortBy("customers")} icon={Users} />
                    <SortButton active={sortBy === "revenue"} label="Highest Revenue" onClick={() => setSortBy("revenue")} icon={IndianRupee} />
                </div>

                {/* Performance Table */}
                <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-xl border border-gray-100 dark:border-slate-700 overflow-hidden">
                    {loading ? (
                        <EmptyState
                            preset="tracking"
                            title="Analyzing performance data..."
                            message="Please wait a moment while we aggregate sales & activity logs."
                            className="py-12"
                        />
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-left">
                                <thead className="bg-gray-50 dark:bg-slate-900/50 border-b border-gray-100 dark:border-slate-700">
                                    <tr>
                                        <th className="px-8 py-5 text-xs font-bold uppercase tracking-widest text-gray-400">Employee Profile</th>
                                        <th className="px-8 py-5 text-xs font-bold uppercase tracking-widest text-gray-400">Performance Status</th>
                                        <th className="px-8 py-5 text-xs font-bold uppercase tracking-widest text-gray-400">Total Bills</th>
                                        <th className="px-8 py-5 text-xs font-bold uppercase tracking-widest text-gray-400">Total Customers</th>
                                        <th className="px-8 py-5 text-xs font-bold uppercase tracking-widest text-gray-400">Total Revenue</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                                    {sortedData.map((member, index) => (
                                        <PerformanceRow 
                                            key={member.id} 
                                            member={member} 
                                            isTop={index === 0} 
                                            isLow={index === sortedData.length - 1 && sortedData.length > 1}
                                        />
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function PerformanceRow({ member, isTop, isLow }) {
    return (
        <tr className={`transition-colors ${isTop ? "bg-green-50/30 dark:bg-green-900/5" : isLow ? "bg-red-50/30 dark:bg-red-900/5" : "hover:bg-gray-50 dark:hover:bg-slate-700/30"}`}>
            <td className="px-8 py-6">
                <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center font-bold text-lg shadow-sm ${
                        isTop ? "bg-green-600 text-white" : isLow ? "bg-red-600 text-white" : "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400"
                    }`}>
                        {member.first_name?.[0]}{member.last_name?.[0]}
                    </div>
                    <div>
                        <p className="font-bold text-gray-900 dark:text-white text-lg">{member.first_name} {member.last_name}</p>
                        <p className="text-xs text-gray-500 font-medium uppercase tracking-tight">
                            {getRoleLabel(member.roles)} {member.store_details && `• ${member.store_details.name}`}
                        </p>
                    </div>
                </div>
            </td>
            <td className="px-8 py-6">
                <div className="flex flex-col gap-2">
                    {isTop && (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-[10px] font-black uppercase tracking-wider w-fit">
                            <Award className="w-3 h-3" />
                            🏆 Top Performer
                        </span>
                    )}
                    {isLow && (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 text-[10px] font-black uppercase tracking-wider w-fit">
                            <AlertTriangle className="w-3 h-3" />
                            ⚠️ Low Performance
                        </span>
                    )}
                    {!isTop && !isLow && (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gray-100 text-gray-600 dark:bg-slate-700 dark:text-slate-400 text-[10px] font-black uppercase tracking-wider w-fit">
                            ⚖️ Average Consistency
                        </span>
                    )}
                </div>
            </td>
            <td className="px-8 py-6">
                <div className="space-y-1">
                    <p className="text-2xl font-black text-gray-900 dark:text-white capitalize">{member.total_bills || 0}</p>
                    <div className="flex items-center gap-1 text-[10px] text-green-600 font-bold">
                        <TrendingUp className="w-3 h-3" />
                        <span>+{member.today_bills || 0} TODAY</span>
                    </div>
                </div>
            </td>
            <td className="px-8 py-6">
                <div className="space-y-1">
                    <p className="text-2xl font-black text-gray-900 dark:text-white capitalize">{member.total_customers || 0}</p>
                    <div className="flex items-center gap-1 text-[10px] text-blue-600 font-bold">
                        <TrendingUp className="w-3 h-3" />
                        <span>+{member.today_customers || 0} TODAY</span>
                    </div>
                </div>
            </td>
            <td className="px-8 py-6">
                <div className="space-y-1">
                    <div className="flex items-center gap-1 text-2xl font-black text-gray-900 dark:text-white">
                        <IndianRupee className="w-5 h-5" />
                        {member.total_revenue?.toLocaleString() || 0}
                    </div>
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">Gross Completed Sales</p>
                </div>
            </td>
        </tr>
    );
}

function FilterChip({ active, label, onClick }) {
    return (
        <button 
            onClick={onClick}
            className={`px-4 py-2 rounded-xl text-sm font-bold transition-all ${
                active 
                    ? "bg-indigo-600 text-white shadow-md shadow-indigo-500/20" 
                    : "text-gray-500 hover:text-gray-900 dark:hover:text-white hover:bg-white dark:hover:bg-slate-800"
            }`}
        >
            {label}
        </button>
    );
}

function SortButton({ active, label, icon: Icon, onClick }) {
    return (
        <button 
            onClick={onClick}
            className={`flex items-center gap-2 px-5 py-3 rounded-2xl border-2 transition-all ${
                active 
                    ? "bg-indigo-600 border-indigo-600 text-white shadow-xl shadow-indigo-500/20" 
                    : "bg-white dark:bg-slate-800 border-gray-100 dark:border-slate-700 text-gray-600 dark:text-slate-400 hover:border-indigo-300 dark:hover:border-slate-600"
            }`}
        >
            <Icon className="w-4 h-4" />
            <span className="text-sm font-black uppercase tracking-tight">{label}</span>
        </button>
    );
}

function MetricCard({ label, value, sub, icon: Icon, color }) {
    return (
        <div className="bg-white dark:bg-slate-800 p-8 rounded-3xl border border-gray-100 dark:border-slate-700 shadow-sm flex items-center gap-6">
            <div className={`p-4 rounded-2xl bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400`}>
                <Icon className="w-8 h-8" />
            </div>
            <div>
                <p className="text-sm font-bold text-gray-400 uppercase tracking-widest">{label}</p>
                <div className="flex items-baseline gap-2">
                    <p className="text-3xl font-black text-gray-900 dark:text-white">{value}</p>
                    <p className="text-xs text-gray-500 font-medium">{sub}</p>
                </div>
            </div>
        </div>
    );
}

function HighlightCard({ title, employee, metric, type }) {
    if (!employee) return (
        <div className="bg-white dark:bg-slate-800 p-8 rounded-3xl border border-gray-100 dark:border-slate-700 border-dashed animate-pulse" />
    );

    const isTop = type === "top";
    return (
        <div className={`p-8 rounded-3xl border shadow-lg relative overflow-hidden ${
            isTop 
                ? "bg-green-600 border-green-500 text-white shadow-green-500/20" 
                : "bg-red-600 border-red-500 text-white shadow-red-500/20"
        }`}>
            <div className="relative z-10 flex items-center gap-5">
                <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-md flex items-center justify-center font-bold text-2xl">
                    {employee.first_name?.[0]}{employee.last_name?.[0]}
                </div>
                <div>
                    <p className="text-xs font-black uppercase tracking-widest opacity-80 mb-1 flex items-center gap-1">
                        {isTop ? <Star className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                        {title}
                    </p>
                    <p className="text-xl font-bold truncate max-w-[150px]">{employee.first_name} {employee.last_name}</p>
                    <p className="text-sm font-medium opacity-90 mt-1">
                        Highest {metric}: {isTop ? Math.max(employee.total_bills || 0, employee.total_customers || 0) : Math.min(employee.total_bills || 0, employee.total_customers || 0)}
                    </p>
                </div>
            </div>
            {/* Background Icon */}
            {isTop ? (
                <Award className="absolute -right-4 -bottom-4 w-32 h-32 opacity-10 rotate-12" />
            ) : (
                <AlertTriangle className="absolute -right-4 -bottom-4 w-32 h-32 opacity-10 -rotate-12" />
            )}
        </div>
    );
}
